import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../store/store';
import {
    Box,
    Paper,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    CircularProgress,
    Alert,
    Checkbox,
    ListItemText,
    Typography,
} from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridToolbarContainer,
    GridToolbarQuickFilter,
    GridToolbarExport,
    GridToolbarColumnsButton
} from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import { reportsStyles } from './Reports.styles';
import { calculateRowHeight } from '../../../utils/getRowHeightReportsTable';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Trip,
    CarOption,
    TripStatus,
    SearchParams,
} from './Reports.types';
import {
    fetchCars,
    fetchTripTypes,
    searchTrips,
    setDateType,
    setFromDate,
    setToDate,
    setCarTypeId,
    setSelectedStatuses,
    setSelectedTripTypes,
    setSelectedLeadSources,
    setError,
    setInitialized
} from '../../../store/slices/reportsSlice';

interface ReportsProps {
    onTripClick?: (trip: any) => void;
    onTabChange?: (tabId: string) => void;
}

const Reports: React.FC<ReportsProps> = ({ onTripClick, onTabChange }) => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        loading,
        error,
        trips,
        cars,
        dateType,
        fromDate,
        toDate,
        carTypeId,
        selectedStatuses,
        selectedTripTypes,
        selectedLeadSources,
        tripTypes,
        initialized
    } = useSelector((state: RootState) => state.reports);
    const statusOptions: TripStatus[] = [
        'CONFIRMED', 'CLOSED', 'PENDING', 'COMPLETED', 'IN CANCELLATION', 'CANCELLED', 'DRIVER ASSIGNED', 'IN PROGRESS', 'HOT LEAD', 'COLD LEAD', 'JUNK', 'AUDITED',
    ];
    const leadSourceOptions: string[] = ['onotravels', 'Google', 'Justdial', 'Reference', 'Existing Customer', 'Others'];

    const handleTripCodeClick = (tripCode: string, trip: any) => {
        if (onTripClick && onTabChange) {
            // Add the trip to open tabs
            onTripClick({
                tripId: tripCode,
                tripCode: tripCode,
                tripType: 'TRIP_DETAILS',
            });
            // Switch to the trip tab
            onTabChange(tripCode);
        }
    };

    // Custom Toolbar without Filter and Density options
    const CustomToolbar = () => {
        return (
            <GridToolbarContainer sx={{ justifyContent: 'space-between', padding: '8px' }}>
                {/* <Box sx={{ display: 'flex', gap: 1 }}>
                    <GridToolbarQuickFilter />
                </Box> */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadExcel}
                        disabled={loading || rows.length === 0}
                        sx={{
                            color: '#1976d2',
                            borderColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                borderColor: '#1976d2',
                            },
                        }}
                        variant="outlined"
                    >
                        Download Excel
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <GridToolbarColumnsButton />
                    {/* <GridToolbarExport /> */}
                </Box>
            </GridToolbarContainer>
        );
    };

    // Helper function to format dates
    const formatDateForPayload = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        if (isEndDate) {
            return `${year}-${month}-${day}T23:59:59`;
        } else {
            return `${year}-${month}-${day}T00:00:00`;
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleSearch = useCallback(async (): Promise<void> => {
        if (!fromDate || !toDate || carTypeId === null || carTypeId === undefined || selectedStatuses.length === 0) {
            dispatch(setError('Please fill all required fields'));
            return;
        }

        try {
            const fromDateObj = new Date(fromDate);
            const toDateObj = new Date(toDate);

            const searchParams: SearchParams = {
                dateType,
                fromDate: formatDateForPayload(fromDateObj, false),
                toDate: formatDateForPayload(toDateObj, true),
                carTypeId,
                statuses: selectedStatuses,
                tripTypes: selectedTripTypes,
                leadSources: selectedLeadSources,
            };
            await dispatch(searchTrips(searchParams));
        } catch (err) {
            console.error("Error in handleSearch:", err);
        }
    }, [dispatch, dateType, fromDate, toDate, carTypeId, selectedStatuses, selectedTripTypes, selectedLeadSources]);

    // Initialize data on component mount
    useEffect(() => {
        if (!initialized) {
            dispatch(fetchCars());
            dispatch(fetchTripTypes());
            dispatch(setInitialized(true));
            dispatch(setSelectedLeadSources(leadSourceOptions)); // Select all lead sources by default
        }
    }, [dispatch, initialized]);

    // Perform initial search when cars are loaded
    useEffect(() => {
        const performInitialSearch = async () => {
            setTimeout(() => {
                if (fromDate && toDate && carTypeId && selectedStatuses.length > 0 && selectedTripTypes.length > 0 && selectedLeadSources.length > 0) {
                    handleSearch();
                }
            }, 500);
        };

        if (cars.length > 0 && tripTypes.length > 0 && initialized) {
            performInitialSearch();
        }
    }, [cars, tripTypes, initialized, selectedTripTypes.length, selectedLeadSources.length]);

    // Define DataGrid columns
    const columns: GridColDef[] = [
        {
            field: 'tripCode',
            headerName: 'Trip Code',
            width: 120,
            headerClassName: 'datagrid-header',
            renderCell: (params: GridRenderCellParams) => {
                const isDisabled = params.row.serviceType === "OTHERS";
                return (
                    <Box
                        sx={{
                            cursor: isDisabled ? "default" : "pointer",
                            // color: isDisabled ? "grey" : "inherit",
                            "&:hover": isDisabled ? {} : { color: "#1976d2" }
                        }}
                        onClick={() => {
                            if (!isDisabled) {
                                handleTripCodeClick(params.value, params.row);
                            }
                        }}
                    >
                        {params.value}
                    </Box>
                );
            }

        },
        {
            field: 'tripStatus',
            headerName: 'Status',
            width: 150,
            headerClassName: 'datagrid-header',
            renderCell: (params: GridRenderCellParams) => (
              params.value ? ( <Chip
                    label={params.value}
                    size="small"
                    sx={reportsStyles.getStatusChipStyle(params.value)}
                />): null
            ),
        },
        {
            field: 'bookingDate',
            headerName: 'Booking/Created DateTime',
            width: 180,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'startDate',
            headerName: 'PickUp DateTime',
            width: 180,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'endDate',
            headerName: 'End DateTime',
            width: 180,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'serviceType',
            headerName: 'Service Type',
            width: 150,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'linkedTripCode',
            headerName: 'Linked Trip',
            width: 150,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'carType',
            headerName: 'Car Type',
            width: 200,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'carAssigned',
            headerName: 'Car Assigned',
            width: 200,
            headerClassName: 'datagrid-header',
        },

        {
            field: 'operatorName',
            headerName: 'Operator Name',
            width: 180,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'leadSource',
            headerName: 'Lead Source',
            width: 120,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'notes',
            headerName: 'Additional Notes',
            width: 430,
            headerClassName: 'datagrid-header',
            renderCell: (params: GridRenderCellParams) => (
                <div style={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    lineHeight: '1.2',
                    padding: '2px 0',
                }} title={params.value}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'sourceCity',
            headerName: 'Source City',
            width: 150,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'destinationCities',
            headerName: 'Dest. City(s)',
            width: 310,
            headerClassName: 'datagrid-header',
            renderCell: (params: GridRenderCellParams) => (
                <div style={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    lineHeight: '1.2',
                    padding: '2px 0',
                }} title={params.value}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'discount',
            headerName: 'Discount',
            width: 100,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'customerName',
            headerName: 'Customer Name',
            width: 150,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'customerMobile',
            headerName: 'Customer Mobile',
            width: 140,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'operatorMobile',
            headerName: 'Operator Mobile',
            width: 140,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'startOdometer',
            headerName: 'Start Fuel',
            width: 120,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'pickUpOdometer',
            headerName: 'PickUp Fuel',
            width: 120,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'dropOffOdometer',
            headerName: 'DropOff Fuel',
            width: 120,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'endOdometer',
            headerName: 'End Fuel',
            width: 110,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'paymentReceived',
            headerName: 'Payment Received',
            width: 120,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'expenses',
            headerName: 'Expenses',
            width: 110,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'extraCharges',
            headerName: 'Extra Charges',
            width: 110,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'agreedTripFare',
            headerName: 'Agreed Fare',
            width: 120,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'totalFare',
            headerName: 'Total Fare',
            width: 110,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'margin',
            headerName: 'Margin',
            width: 110,
            headerClassName: 'datagrid-header',
        },
        {
            field: 'marginPercentage',
            headerName: 'Margin(%)',
            width: 130,
            headerClassName: 'datagrid-header',
        },
    ];

    // Transform trips data for DataGrid
    const dataRows = trips.map((trip: Trip) => {
        const paymentReceived = (trip?.transactions || []).filter(tx => tx.status == 'SUCCESS').reduce((sum, tx) => sum + (tx.amount || 0), 0);
        const expenses = trip?.expenses?.reduce((sum, expense) => sum + (expense.amount || 0), 0) || 0;
        const extraCharges = trip?.extraCharges?.reduce((sum, charge) => sum + (charge.amount || 0), 0) || 0;
        const agreedTripFare = trip?.tripPricingSnapshot?.[0]?.agreedTripFare || 0;
        const totalFare = agreedTripFare + extraCharges;
        const margin = totalFare - expenses;
        const marginPercentage = totalFare > 0 ? ((margin / totalFare) * 100).toFixed(2) : '0.00';
        
        return {
            id: trip.id,
            tripCode: trip.tripCode || 'N/A',
            tripStatus: trip.tripStatus || 'N/A',
            customerName: trip?.customer?.name || 'N/A',
            customerEmail: trip?.customer?.email || 'N/A',
            customerMobile: trip?.customer?.mobileNumber || 'N/A',
            operatorName: `${trip?.operator?.firstName || 'N/A'} ${trip?.operator?.lastName || ''}`.trim(),
            operatorEmail: trip?.operator?.email || 'N/A',
            operatorMobile: trip?.operator?.mobileNumber || 'N/A',
            carAssigned: (trip?.car?.carType?.makeCompany || trip?.car?.carType?.model)
                ? `${trip.car.carType.makeCompany} ${trip.car.carType.model} ${trip.car.plateNumber ? ` - ${trip.car.plateNumber}` : ''}`.trim()
                : 'N/A',
            carType: (trip?.travelServiceDetail?.carType?.makeCompany || trip?.travelServiceDetail?.carType?.model)
                ? `${trip.travelServiceDetail.carType.makeCompany}${trip.travelServiceDetail.carType.model}`
                : 'N/A',
            serviceType: trip?.travelServiceDetail?.travelService?.name || 'N/A',
            startDate: formatDate(trip?.startDatetime) || 'N/A',
            endDate: formatDate(trip?.endDatetime) || 'N/A',
            bookingDate: formatDate(trip?.bookingDatetime) || 'N/A',
            startOdometer: trip?.startOdometer || 'N/A',
            pickUpOdometer: trip?.pickUpOdometer || 'N/A',
            dropOffOdometer: trip?.dropOffOdometer || 'N/A',
            endOdometer: trip?.endOdometer || 'N/A',
            linkedTripCode: trip?.linkedTripCode || 'N/A',
            sourceCity: trip?.sourceCity || 'N/A',
            destinationCities: trip?.destinationCities || 'N/A',
            leadSource: trip?.request || 'N/A',
            notes: trip?.notes || 'N/A',
            agreedTripFare: agreedTripFare,
            discount: trip?.tripPricingSnapshot?.[0]?.discount || 0,
            paymentReceived: paymentReceived,
            expenses: expenses,
            extraCharges: extraCharges,
            totalFare: totalFare,
            margin: margin,
            marginPercentage: marginPercentage,
        };
    });

    // Calculate totals for amount-related fields
    const totals = dataRows.reduce((acc, row) => ({
        discount: acc.discount + (Number(row.discount) || 0),
        paymentReceived: acc.paymentReceived + (Number(row.paymentReceived) || 0),
        expenses: acc.expenses + (Number(row.expenses) || 0),
        extraCharges: acc.extraCharges + (Number(row.extraCharges) || 0),
        agreedTripFare: acc.agreedTripFare + (Number(row.agreedTripFare) || 0),
        totalFare: acc.totalFare + (Number(row.totalFare) || 0),
        margin: acc.margin + (Number(row.margin) || 0),
    }), { discount: 0, paymentReceived: 0, expenses: 0, extraCharges: 0, agreedTripFare: 0, totalFare: 0, margin: 0 });

    // Add total row at the end
    const totalRow = {
        id: 'total-row',
        tripCode: 'TOTAL',
        tripStatus: '',
        customerName: '',
        customerEmail: '',
        customerMobile: '',
        operatorName: '',
        operatorEmail: '',
        operatorMobile: '',
        carAssigned: '',
        carType: '',
        serviceType: '',
        startDate: '',
        endDate: '',
        bookingDate: '',
        startOdometer: '',
        pickUpOdometer: '',
        dropOffOdometer: '',
        endOdometer: '',
        linkedTripCode: '',
        sourceCity: '',
        destinationCities: '',
        leadSource: '',
        notes: '',
        discount: totals.discount,
        paymentReceived: totals.paymentReceived,
        expenses: totals.expenses,
        extraCharges: totals.extraCharges,
        agreedTripFare: totals.agreedTripFare,
        totalFare: totals.totalFare,
        margin: totals.margin,
        marginPercentage: totals.totalFare > 0 ? ((totals.margin / totals.totalFare) * 100).toFixed(2) : '0.00',
    };

    // Combine data rows with total row
    const rows = dataRows.length > 0 ? [...dataRows, totalRow] : [];

// ...existing code...

    // Add this helper function to handle "All" selection
    const handleStatusChange = (selectedValues: string[]) => {
        if (selectedValues.includes('ALL')) {
            if (selectedStatuses.length === statusOptions.length) {
                dispatch(setSelectedStatuses([]));
            } else {

                dispatch(setSelectedStatuses(statusOptions));
            }
        } else {
            const filteredValues = selectedValues.filter(value => value !== 'ALL');
            dispatch(setSelectedStatuses(filteredValues));
        }
    };

    const handleTripTypesChange = (selectedValues: string[]) => {
        if (selectedValues.includes('ALL')) {
            if (selectedTripTypes.length === tripTypes.length) {
                dispatch(setSelectedTripTypes([]));
            } else {
                const allTripTypeIds = tripTypes.map((tt: any) => tt.id?.toString());
                dispatch(setSelectedTripTypes(allTripTypeIds));
            }
        } else {
            const filteredValues = selectedValues.filter(value => value !== 'ALL');
            dispatch(setSelectedTripTypes(filteredValues));
        }
    };

    const handleLeadSourceChange = (selectedValues: string[]) => {
        if (selectedValues.includes('ALL')) {
            if (selectedLeadSources.length === leadSourceOptions.length) {
                dispatch(setSelectedLeadSources([]));
            } else {
                dispatch(setSelectedLeadSources(leadSourceOptions));
            }
        } else {
            const filteredValues = selectedValues.filter(value => value !== 'ALL');
            dispatch(setSelectedLeadSources(filteredValues));
        }
    };

    // Function to download data as Excel
    const handleDownloadExcel = useCallback(() => {
        if (rows.length === 0) {
            dispatch(setError('No data available to download'));
            return;
        }

        try {
            // Prepare data for Excel export
            const excelData = rows.map((row) => ({
                'Trip Code': row.tripCode,
                'Status': row.tripStatus,
                'Booking/Created DateTime': row.bookingDate,
                'PickUp DateTime': row.startDate,
                'End DateTime': row.endDate,
                'Service Type': row.serviceType,
                'Linked Trip': row.linkedTripCode,
                'Car Type': row.carType,
                'Car Assigned': row.carAssigned,
                'Operator Name': row.operatorName,
                'Lead Source': row.leadSource,
                'Additional Notes': row.notes,
                'Source City': row.sourceCity,
                'Destination City(s)': row.destinationCities,
                'Discount': row.discount,
                'Customer Name': row.customerName,
                'Customer Mobile': row.customerMobile,
                'Operator Mobile': row.operatorMobile,
                'Start Fuel': row.startOdometer,
                'PickUp Fuel': row.pickUpOdometer,
                'DropOff Fuel': row.dropOffOdometer,
                'End Fuel': row.endOdometer,
                'Payment Received': row.paymentReceived,
                'Expenses': row.expenses,
                'Extra Charges': row.extraCharges,
                'Agreed Fare': row.agreedTripFare,
                'Total Fare': row.totalFare,
                'Margin': row.margin,
                'Margin(%)': row.marginPercentage,
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths for better readability
            const columnWidths = [
                { wch: 15 }, // Trip Code
                { wch: 12 }, // Status
                { wch: 18 }, // Start Date
                { wch: 15 }, // Service Type
                { wch: 20 }, // Car Assigned
                { wch: 20 }, // Car Type
                { wch: 18 }, // Operator Name
                { wch: 12 }, // Lead Source
                { wch: 50 }, // Additional Notes
                { wch: 15 }, // Source City
                { wch: 30 }, // Destination Cities
                { wch: 12 }, // Agreed Fare
                { wch: 10 }, // Discount
                { wch: 18 }, // Customer Name
                { wch: 15 }, // Customer Mobile
                { wch: 15 }, // Operator Mobile
                { wch: 12 }, // Start Fuel
                { wch: 12 }, // PickUp Fuel
                { wch: 12 }, // DropOff Fuel
                { wch: 12 }, // End Fuel
                { wch: 15 }, // Payment Received
                { wch: 12 }, // Expenses
            ];

            ws['!cols'] = columnWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Trip Reports');

            // Generate filename with current date
            const currentDate = new Date().toISOString().split('T')[0];
            const filename = `Trip_Reports_${currentDate}.xlsx`;

            // Download the file
            XLSX.writeFile(wb, filename);
        } catch (error) {
            console.error('Error downloading Excel file:', error);
            dispatch(setError('Failed to download Excel file'));
        }
    }, [rows, dispatch]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{
                ...reportsStyles.mainContainer,
                padding: '16px',
                height: 'calc(100vh - 32px)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* <Typography variant="h5" gutterBottom sx={reportsStyles.title}>
                    Trip Reports
                </Typography> */}

                {/* Filter Header */}
                <Paper sx={reportsStyles.filterPaper}>
                    <Box sx={reportsStyles.filterContainer}>
                        {/* Date Type */}
                        <Box sx={reportsStyles.dateTypeContainer}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={reportsStyles.formControlLabel}>Date Type</InputLabel>
                                <Select
                                    value={dateType}
                                    onChange={(e) => dispatch(setDateType(e.target.value as 'PICKUP' | 'BOOKING'))}
                                    label="Date Type"
                                    sx={reportsStyles.selectField}
                                >
                                    <MenuItem value="PICKUP">Pickup</MenuItem>
                                    <MenuItem value="BOOKING">Booking</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* From Date */}
                        <Box sx={reportsStyles.dateContainer}>
                            <DatePicker
                                label="From Date"
                                format="dd/MM/yyyy"
                                value={fromDate ? new Date(fromDate) : null}
                                onChange={(date) => dispatch(setFromDate(date ? date.toISOString() : null))}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: reportsStyles.datePickerField
                                    }
                                }}
                            />
                        </Box>

                        {/* To Date */}
                        <Box sx={reportsStyles.dateContainer}>
                            <DatePicker
                                label="To Date"
                                format="dd/MM/yyyy"
                                value={toDate ? new Date(toDate) : null}
                                onChange={(date) => dispatch(setToDate(date ? date.toISOString() : null))}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: reportsStyles.datePickerField
                                    }
                                }}
                            />
                        </Box>

                        {/* Car ID */}
                        <Box sx={reportsStyles.carContainer}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={reportsStyles.formControlLabel}>Car Type</InputLabel>
                                <Select
                                    value={carTypeId}
                                    onChange={(e) => dispatch(setCarTypeId(Number(e.target.value)))}
                                    label="Car Type"
                                    sx={reportsStyles.selectField}
                                >
                                    {cars.length === 0 && loading ? (
                                        <MenuItem disabled>Loading cars...</MenuItem>
                                    ) : (
                                        [
                                            <MenuItem key="all" value={0}>
                                                All
                                            </MenuItem>,
                                            ...cars
                                                .filter((car, index, self) =>
                                                    index === self.findIndex(c => c.carType.id === car.carType.id)
                                                )
                                                .map((car: CarOption) => (
                                                    <MenuItem key={car.carType.id} value={car.carType.id}>
                                                        {`${car.carType.makeCompany} ${car.carType.model}`}
                                                    </MenuItem>
                                                ))
                                        ]
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        {/* Trip Types Multi-select */}
                        <Box sx={reportsStyles.statusContainer}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={reportsStyles.formControlLabel}>Trip Types</InputLabel>
                                <Select
                                    multiple
                                    value={selectedTripTypes}
                                    onChange={(e) => handleTripTypesChange(e.target.value as string[])}
                                    input={<OutlinedInput label="Trip Types" />}
                                    sx={reportsStyles.selectField}
                                    renderValue={(selected: string[]) => {
                                        if (selected.length === 0) {
                                            return <em>Select trip types</em>;
                                        }
                                        const tripTypesList = tripTypes || [];
                                        if (selected.length === tripTypesList.length) {
                                            return (
                                                <Box sx={reportsStyles.statusChipContainer}>
                                                    <Chip
                                                        label="All Selected"
                                                        size="small"
                                                        sx={{ ...reportsStyles.statusChip, backgroundColor: '#1976d2', color: 'white' }}
                                                    />
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box sx={reportsStyles.statusChipContainer}>
                                                {selected.map((value) => {
                                                    const tripType = tripTypesList.find((tt: any) => tt.id?.toString() === value);
                                                    return (
                                                        <Chip
                                                            key={value}
                                                            label={tripType?.name || value}
                                                            size="small"
                                                            sx={reportsStyles.statusChip}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        );
                                    }}
                                >
                                    <MenuItem key="ALL" value="ALL" sx={{ py: 0.5, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
                                        <Checkbox
                                            checked={selectedTripTypes.length === (tripTypes || []).length}
                                            indeterminate={selectedTripTypes.length > 0 && selectedTripTypes.length < (tripTypes || []).length}
                                            size="small"
                                            sx={{
                                                color: '#666',
                                                '&.Mui-checked': {
                                                    color: '#1976d2',
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: '#1976d2',
                                                },
                                                mr: 1
                                            }}
                                        />
                                        <ListItemText
                                            primary="All"
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        />
                                    </MenuItem>
                                    {(tripTypes || []).map((tripType: any) => (
                                            <MenuItem key={tripType.id} value={tripType.id?.toString()} sx={{ py: 0.5 }}>
                                                <Checkbox
                                                    checked={selectedTripTypes.indexOf(tripType.id?.toString()) > -1}
                                                    size="small"
                                                    sx={{
                                                        color: '#666',
                                                        '&.Mui-checked': {
                                                            color: '#1976d2',
                                                        },
                                                        mr: 1
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={tripType.name}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '0.875rem'
                                                        }
                                                    }}
                                                />
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {/* Status Multi-select */}
                        <Box sx={reportsStyles.statusContainer}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={reportsStyles.formControlLabel}>Status</InputLabel>
                                <Select
                                    multiple
                                    value={selectedStatuses}
                                    onChange={(e) => handleStatusChange(e.target.value as string[])}
                                    input={<OutlinedInput label="Status" />}
                                    sx={reportsStyles.selectField}
                                    renderValue={(selected: string[]) => {
                                        // if (selected.length === 0) {
                                        //     return <em>Select statuses</em>;
                                        // }
                                        if (selected.length === statusOptions.length) {
                                            return (
                                                <Box sx={reportsStyles.statusChipContainer}>
                                                    <Chip
                                                        label="All Selected"
                                                        size="small"
                                                        sx={{ ...reportsStyles.statusChip, backgroundColor: '#1976d2', color: 'white' }}
                                                    />
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box sx={reportsStyles.statusChipContainer}>
                                                {selected.map((value) => (
                                                    <Chip
                                                        key={value}
                                                        label={value}
                                                        size="small"
                                                        sx={reportsStyles.statusChip}
                                                    />
                                                ))}
                                            </Box>
                                        );
                                    }}
                                >
                                    <MenuItem key="ALL" value="ALL" sx={{ py: 0.5, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
                                        <Checkbox
                                            checked={selectedStatuses.length === statusOptions.length}
                                            indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < statusOptions.length}
                                            size="small"
                                            sx={{
                                                color: '#666',
                                                '&.Mui-checked': {
                                                    color: '#1976d2',
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: '#1976d2',
                                                },
                                                mr: 1
                                            }}
                                        />
                                        <ListItemText
                                            primary="All"
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        />
                                    </MenuItem>
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status} value={status} sx={{ py: 0.5 }}>
                                            <Checkbox
                                                checked={selectedStatuses.indexOf(status) > -1}
                                                size="small"
                                                sx={{
                                                    color: '#666',
                                                    '&.Mui-checked': {
                                                        color: '#1976d2',
                                                    },
                                                    mr: 1
                                                }}
                                            />
                                            <ListItemText
                                                primary={status}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {/* Lead Source Multi-select */}
                        <Box sx={reportsStyles.leadSourceContainer}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={reportsStyles.formControlLabel}>Lead Source</InputLabel>
                                <Select
                                    multiple
                                    value={selectedLeadSources}
                                    onChange={(e) => handleLeadSourceChange(e.target.value as string[])}
                                    input={<OutlinedInput label="Lead Source" />}
                                    sx={reportsStyles.selectField}
                                    renderValue={(selected: string[]) => {
                                        if (selected.length === 0) {
                                            return <em>Select sources</em>;
                                        }
                                        if (selected.length === leadSourceOptions.length) {
                                            return (
                                                <Box sx={reportsStyles.statusChipContainer}>
                                                    <Chip
                                                        label="All Selected"
                                                        size="small"
                                                        sx={{ ...reportsStyles.statusChip, backgroundColor: '#1976d2', color: 'white' }}
                                                    />
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box sx={reportsStyles.statusChipContainer}>
                                                {selected.map((value) => (
                                                    <Chip
                                                        key={value}
                                                        label={value}
                                                        size="small"
                                                        sx={reportsStyles.statusChip}
                                                    />
                                                ))}
                                            </Box>
                                        );
                                    }}
                                >
                                    <MenuItem key="ALL" value="ALL" sx={{ py: 0.5, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
                                        <Checkbox
                                            checked={selectedLeadSources.length === leadSourceOptions.length}
                                            indeterminate={selectedLeadSources.length > 0 && selectedLeadSources.length < leadSourceOptions.length}
                                            size="small"
                                            sx={{
                                                color: '#666',
                                                '&.Mui-checked': {
                                                    color: '#1976d2',
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: '#1976d2',
                                                },
                                                mr: 1
                                            }}
                                        />
                                        <ListItemText
                                            primary="All"
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        />
                                    </MenuItem>
                                    {leadSourceOptions.map((source) => (
                                        <MenuItem key={source} value={source} sx={{ py: 0.5 }}>
                                            <Checkbox
                                                checked={selectedLeadSources.indexOf(source) > -1}
                                                size="small"
                                                sx={{
                                                    color: '#666',
                                                    '&.Mui-checked': {
                                                        color: '#1976d2',
                                                    },
                                                    mr: 1
                                                }}
                                            />
                                            <ListItemText
                                                primary={source}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Search Button */}
                        <Box sx={reportsStyles.searchButtonContainer}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSearch}
                                disabled={loading}
                                startIcon={<SearchIcon />}
                                sx={reportsStyles.searchButton}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Error Display */}
                {error && (
                    <Alert severity="error" sx={reportsStyles.errorAlert}>
                        {error}
                    </Alert>
                )}

                {/* DataGrid Results */}
                <Paper sx={{ ...reportsStyles.dataGridPaper, marginTop: error ? 0 : 1 }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 25 },
                            },
                        }}
                        slots={{ toolbar: CustomToolbar }}
                        // hideFooterPagination
                        disableRowSelectionOnClick
                        disableColumnSorting
                        disableColumnMenu
                        // getRowHeight={() => 'auto'}
                        getRowHeight={calculateRowHeight}
                        autosizeOnMount
                        getRowClassName={(params) => params.id === 'total-row' ? 'total-row' : ''}
                        sx={{
                            height: '100%',
                            border: 'none',
                            '& .MuiDataGrid-virtualScroller': {
                                overflowY: 'auto',
                                overflowX: 'auto'
                            },
                            '& .MuiDataGrid-footerContainer': {
                                backgroundColor: '#1a1a1a',
                                borderTop: '1px solid #404040',
                                minHeight: '52px'
                            },
                            '& .MuiDataGrid-cell': {
                                paddingTop: '8px',
                                paddingBottom: '8px',
                                alignItems: 'flex-start'
                            },
                            '& .total-row': {
                                backgroundColor: '#1a1a1a !important',
                                fontWeight: 'bold',
                                borderTop: '2px solid #2196F3',
                                '& .MuiDataGrid-cell': {
                                    fontWeight: 'bold',
                                    color: '#2196F3',
                                    fontSize: '0.9rem'
                                },
                                '&:hover': {
                                    backgroundColor: '#252525 !important'
                                }
                            }
                        }}

                    />
                </Paper>
            </Box>
        </LocalizationProvider>
    );
};

export default Reports;